import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Button } from "../ui/button";
import SelectField from "../ui/SelectField";

const COLUMNAS_BASE = [
  { key: "codigo", label: "Código" },
  { key: "serial", label: "Serial" },
  { key: "inventario", label: "Inventario" },
  { key: "cliente", label: "Cliente" },
  { key: "sede", label: "Sede" },
  { key: "grupo", label: "Grupo" },
  { key: "area", label: "Área" },
  { key: "categoria", label: "Categoría" },
  { key: "marca", label: "Marca" },
  { key: "modelo", label: "Modelo" },
  { key: "marcaModelo", label: "Marca / Modelo" },
  { key: "propiedad", label: "Propiedad" },
  { key: "condicion", label: "Condición" },
  { key: "garantia", label: "Garantía" },
  { key: "proveedor", label: "Proveedor" },
  { key: "costo", label: "Costo" },
  { key: "fechaCompra", label: "Fecha compra" },
  { key: "responsable", label: "Responsable actual" },
  { key: "accesorios", label: "Componentes/Accesorios" },
  { key: "documentos", label: "Documentos" },
  { key: "invima", label: "INVIMA" },
  { key: "claseTecno", label: "Clase Tecnológica" },
  { key: "claseRiesgo", label: "Clase de Riesgo" },
  { key: "clasificacion", label: "Clasificación" },
  { key: "usos", label: "Usos" },
  { key: "periocidad_mantenimiento", label: "Periodicidad Mantenimiento" },
  { key: "periocidad_calibracion", label: "Periodicidad Calibración" },
];

const ModalInventarioDescarga = ({ onClose }) => {
  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const [isLoading, setIsLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [sedeGrupos, setSedeGrupos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [columnasDinamicas, setColumnasDinamicas] = useState([]);
  const [metaCaracteristicas, setMetaCaracteristicas] = useState([]);

  const [filtros, setFiltros] = useState({
    clienteId: "all",
    sedeGrupoId: "all",
    sedeId: "all",
    grupoId: "all",
    areaId: "all",
  });

  const [columnasSeleccionadas, setColumnasSeleccionadas] = useState(
    COLUMNAS_BASE.map((c) => c.key),
  );

  // Drag & drop state
  const dragIndex = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => {
      const nuevos = { ...prev, [name]: value || "all" };
      if (name === "clienteId") {
        nuevos.sedeId = "all";
        nuevos.areaId = "all";
      }
      if (name === "sedeGrupoId") {
        nuevos.sedeId = "all";
      }
      if (name === "grupoId") {
        nuevos.areaId = "all";
      }
      return nuevos;
    });
  };

  useEffect(() => {
    const cargarBase = async () => {
      try {
        const [resClientes, resGrupos, resAreas, resFiltros] =
          await Promise.all([
            axios.get(`${API}/clientes`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${API}/area-grupos`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${API}/areas`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${API}/equipos/filtros`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);

        setClientes(resClientes.data || []);
        setGrupos(resGrupos.data || []);
        setAreas(resAreas.data || []);
        setMetaCaracteristicas(resFiltros.data?.caracteristicas || []);
      } catch {
        toast.error("No se pudo cargar la configuración del inventario");
      }
    };

    const cargarSedeGrupos = async () => {
      try {
        const res = await axios.get(`${API}/sede-grupos`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSedeGrupos(res.data || []);
      } catch {
        setSedeGrupos([]);
      }
    };

    cargarBase();
    cargarSedeGrupos();
  }, [API, token]);

  useEffect(() => {
    const cargarSedes = async () => {
      try {
        if (!clientes.length) return;

        if (filtros.clienteId !== "all") {
          const res = await axios.get(`${API}/sedes/${filtros.clienteId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setSedes(res.data || []);
          return;
        }

        const responses = await Promise.all(
          clientes.map((c) =>
            axios
              .get(`${API}/sedes/${c._id}`, {
                headers: { Authorization: `Bearer ${token}` },
              })
              .then((r) => r.data || []),
          ),
        );

        const unique = new Map();
        responses.flat().forEach((s) => unique.set(String(s._id), s));
        setSedes(Array.from(unique.values()));
      } catch {
        toast.error("No se pudieron cargar las sedes");
      }
    };

    cargarSedes();
  }, [API, token, clientes, filtros.clienteId]);

  useEffect(() => {
    const cargarColumnasDinamicas = async () => {
      try {
        const params = {};
        if (filtros.clienteId !== "all") params.cliente = filtros.clienteId;
        // /equipos prioriza sedeGrupo sobre sede: si hay sede concreta, basta con ella
        if (filtros.sedeId !== "all") params.sede = filtros.sedeId;
        else if (filtros.sedeGrupoId !== "all")
          params.sedeGrupo = filtros.sedeGrupoId;
        if (filtros.areaId !== "all") params.area = filtros.areaId;

        const { data } = await axios.get(`${API}/equipos`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });

        const dynamicKeys = new Set();
        (data || []).forEach((eq) => {
          Object.keys(eq.caracteristicas || {}).forEach((k) =>
            dynamicKeys.add(k),
          );
        });

        const columnas = Array.from(dynamicKeys)
          .sort((a, b) => a.localeCompare(b))
          .map((key) => {
            const meta = metaCaracteristicas.find((m) => m.key === key);
            return {
              key: `caracteristica:${key}`,
              label: meta?.nombre || `Característica: ${key}`,
            };
          });

        setColumnasDinamicas(columnas);
      } catch {
        toast.error("No se pudieron cargar las columnas disponibles");
      }
    };

    cargarColumnasDinamicas();
  }, [API, token, filtros, metaCaracteristicas]);

  const areasFiltradas = useMemo(() => {
    return areas.filter((a) => {
      if (filtros.grupoId !== "all" && a.grupo?._id !== filtros.grupoId) {
        return false;
      }
      return true;
    });
  }, [areas, filtros.grupoId]);

  // Si hay grupo de sedes, la lista de sedes se limita a las del grupo
  const sedesFiltradas = useMemo(() => {
    if (filtros.sedeGrupoId === "all") return sedes;
    const grupo = sedeGrupos.find(
      (g) => String(g._id) === String(filtros.sedeGrupoId),
    );
    const idsGrupo = new Set(
      (grupo?.sedes || []).map((s) => String(s?._id || s)),
    );
    return sedes.filter((s) => idsGrupo.has(String(s._id)));
  }, [sedes, sedeGrupos, filtros.sedeGrupoId]);

  const columnasDisponibles = useMemo(
    () => [...COLUMNAS_BASE, ...columnasDinamicas],
    [columnasDinamicas],
  );

  const toggleColumna = (key) => {
    setColumnasSeleccionadas((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const seleccionarTodasColumnas = () => {
    setColumnasSeleccionadas(columnasDisponibles.map((c) => c.key));
  };

  const limpiarColumnas = () => {
    setColumnasSeleccionadas([]);
  };

  // Drag & drop handlers
  const handleDragStart = (index) => {
    dragIndex.current = index;
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const from = dragIndex.current;
    if (from === null || from === dropIndex) {
      setDragOverIndex(null);
      return;
    }
    setColumnasSeleccionadas((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(dropIndex, 0, moved);
      return next;
    });
    dragIndex.current = null;
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    dragIndex.current = null;
    setDragOverIndex(null);
  };

  const handleDescargar = async (format) => {
    if (!columnasSeleccionadas.length) {
      toast.warning("Selecciona al menos una columna");
      return;
    }

    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("format", format);
      params.append("clienteId", filtros.clienteId || "all");
      params.append("sedeGrupoId", filtros.sedeGrupoId || "all");
      params.append("sedeId", filtros.sedeId || "all");
      params.append("grupoId", filtros.grupoId || "all");
      params.append("areaId", filtros.areaId || "all");
      columnasSeleccionadas.forEach((c) => params.append("columns", c));

      const res = await axios.get(
        `${API}/reportes/inventario?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        },
      );

      const extension = format === "excel" ? "xlsx" : "pdf";
      const mime =
        format === "excel"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf";

      const blob = new Blob([res.data], { type: mime });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `inventario_previonix.${extension}`;
      link.click();

      toast.success(`Inventario descargado en ${format.toUpperCase()}`);
    } catch {
      toast.error("No se pudo descargar el inventario");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-5xl max-h-[92vh] bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">
            Descargar inventario
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Filtra, selecciona columnas y arrástralas para definir el orden del reporte.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Filtros */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <SelectField
              label="Cliente"
              name="clienteId"
              value={filtros.clienteId}
              onChange={handleFiltroChange}
              options={[{ _id: "all", name: "Todos" }, ...clientes]}
            />
            <SelectField
              label="Grupo de sedes"
              name="sedeGrupoId"
              value={filtros.sedeGrupoId}
              onChange={handleFiltroChange}
              options={[{ _id: "all", name: "Todos" }, ...sedeGrupos]}
            />
            <SelectField
              label="Sede"
              name="sedeId"
              value={filtros.sedeId}
              onChange={handleFiltroChange}
              options={[{ _id: "all", name: "Todas" }, ...sedesFiltradas]}
            />
            <SelectField
              label="Grupo de áreas"
              name="grupoId"
              value={filtros.grupoId}
              onChange={handleFiltroChange}
              options={[{ _id: "all", name: "Todos" }, ...grupos]}
            />
            <SelectField
              label="Área"
              name="areaId"
              value={filtros.areaId}
              onChange={handleFiltroChange}
              options={[{ _id: "all", name: "Todas" }, ...areasFiltradas]}
              displayFn={(a) =>
                a._id === "all"
                  ? a.name
                  : a.grupo?.name
                    ? `${a.grupo.name} - ${a.name}`
                    : a.name
              }
            />
          </div>

          {/* Columnas: dos paneles */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Panel izquierdo: selección */}
            <div className="border rounded-xl p-4 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium text-gray-800">Columnas disponibles</h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {columnasSeleccionadas.length} de {columnasDisponibles.length} seleccionadas
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={seleccionarTodasColumnas}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Todas
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={limpiarColumnas}
                    className="text-xs text-gray-500 hover:underline"
                  >
                    Ninguna
                  </button>
                </div>
              </div>

              <div className="space-y-0.5 overflow-auto flex-1 max-h-72 pr-1">
                {columnasDisponibles.map((col) => {
                  const selected = columnasSeleccionadas.includes(col.key);
                  return (
                    <button
                      type="button"
                      key={col.key}
                      onClick={() => toggleColumna(col.key)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                        selected
                          ? "bg-blue-50 text-blue-700"
                          : "text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      <span
                        className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center transition-colors ${
                          selected
                            ? "bg-blue-600 border-blue-600"
                            : "border-gray-300"
                        }`}
                      >
                        {selected && (
                          <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 8" fill="none">
                            <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                      <span className="flex-1">{col.label}</span>
                      {selected && (
                        <span className="text-xs text-blue-400 font-mono">
                          #{columnasSeleccionadas.indexOf(col.key) + 1}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Panel derecho: orden con drag & drop */}
            <div className="border rounded-xl p-4 flex flex-col">
              <div className="mb-3">
                <h3 className="font-medium text-gray-800">Orden en el reporte</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Arrastra para reordenar las columnas
                </p>
              </div>

              {columnasSeleccionadas.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-gray-400 text-sm gap-2 py-8">
                  <svg className="w-8 h-8 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span>Selecciona columnas del panel izquierdo</span>
                </div>
              ) : (
                <div className="space-y-1 overflow-auto flex-1 max-h-72 pr-1">
                  {columnasSeleccionadas.map((key, index) => {
                    const col = columnasDisponibles.find((c) => c.key === key);
                    if (!col) return null;
                    const isDragOver = dragOverIndex === index;
                    return (
                      <div
                        key={key}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-grab active:cursor-grabbing select-none transition-colors ${
                          isDragOver
                            ? "bg-blue-100 border-2 border-blue-400 border-dashed"
                            : "bg-gray-50 border-2 border-transparent hover:bg-gray-100"
                        }`}
                      >
                        {/* Drag handle */}
                        <svg
                          className="w-4 h-4 text-gray-400 flex-shrink-0"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                        >
                          <circle cx="5" cy="4" r="1.2" />
                          <circle cx="5" cy="8" r="1.2" />
                          <circle cx="5" cy="12" r="1.2" />
                          <circle cx="11" cy="4" r="1.2" />
                          <circle cx="11" cy="8" r="1.2" />
                          <circle cx="11" cy="12" r="1.2" />
                        </svg>
                        <span className="w-5 text-center text-gray-400 text-xs font-mono flex-shrink-0">
                          {index + 1}
                        </span>
                        <span className="flex-1 text-gray-700">{col.label}</span>
                        <button
                          type="button"
                          onClick={() => toggleColumna(key)}
                          className="text-gray-300 hover:text-red-400 transition-colors flex-shrink-0"
                          title="Quitar columna"
                        >
                          <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" d="M4 4l8 8M12 4l-8 8" strokeWidth="1.5" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-6 border-t">
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
          <Button
            variant="outline"
            disabled={isLoading}
            onClick={() => handleDescargar("pdf")}
          >
            {isLoading ? "Generando..." : "Descargar PDF"}
          </Button>
          <Button disabled={isLoading} onClick={() => handleDescargar("excel")}>
            {isLoading ? "Generando..." : "Descargar Excel"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ModalInventarioDescarga;
